/**
 * Formato de valores. Los montos llegan como string decimal ("129900.0000"): nunca se opera con
 * number; Intl.NumberFormat formatea strings decimales sin perder precisión.
 */
const ZERO_DECIMAL = new Set(['COP', 'CLP', 'PYG', 'JPY']);

const cache = new Map<string, Intl.NumberFormat>();
function formatter(key: string, factory: () => Intl.NumberFormat): Intl.NumberFormat {
  let f = cache.get(key);
  if (!f) {
    f = factory();
    cache.set(key, f);
  }
  return f;
}

type Decimal = string | number | null | undefined;

function asNumeric(value: Decimal): `${number}` | number {
  if (value === null || value === undefined || value === '') return 0;
  return typeof value === 'number' ? value : (value as `${number}`);
}

export function formatMoney(value: Decimal, currency = 'COP', locale = 'es-CO'): string {
  const digits = ZERO_DECIMAL.has(currency) ? 0 : 2;
  const nf = formatter(
    `m:${locale}:${currency}`,
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }),
  );
  return nf.format(asNumeric(value));
}

export function formatQuantity(value: Decimal, locale = 'es-CO'): string {
  const nf = formatter(
    `q:${locale}`,
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }),
  );
  return nf.format(asNumeric(value));
}

export function formatPercent(value: Decimal, locale = 'es-CO'): string {
  const nf = formatter(
    `p:${locale}`,
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }),
  );
  return `${nf.format(asNumeric(value))} %`;
}

export function formatDate(value: string | null | undefined, locale = 'es-CO'): string {
  if (!value) return '—';
  const date = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);
  // Espacios que no se parten: en una tabla angosta la fecha no ocupa cuatro líneas.
  return date
    .toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })
    .replace(/ /g, ' ');
}

export function formatDateTime(value: string | null | undefined, locale = 'es-CO'): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Espacio que no se parte: el número no queda en dos líneas en una tabla angosta. */
const NBSP = String.fromCharCode(0xa0);

/**
 * Teléfono legible y siempre igual en todo el panel: «+57 300 882 4024». Acepta lo que guarda el
 * sistema («+573008824024», «573008824024» del WhatsApp, «300 882 4024»). Un celular colombiano
 * de 10 dígitos sin indicativo se toma como +57; de otros países solo se antepone el «+». Si no
 * parece un teléfono, se devuelve tal cual.
 */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return '—';
  const digits = value.replace(/\D/g, '');
  if (!/^[+\d\s().-]+$/.test(value.trim()) || digits.length < 7 || digits.length > 15) return value;
  const full = digits.length === 10 && digits.startsWith('3') ? `57${digits}` : digits;
  if (full.length === 12 && full.startsWith('57'))
    return ['+57', full.slice(2, 5), full.slice(5, 8), full.slice(8)].join(NBSP);
  return `+${full}`;
}

/** Comparación de decimales en string sin aritmética flotante acumulada. */
export function isPositive(value: Decimal): boolean {
  return Number(value ?? 0) > 0;
}

export const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
  web: 'Web',
  pos: 'Punto de venta',
  api: 'API',
  other: 'Otro',
};

export const ORDER_STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  confirmed: 'Confirmado',
  fulfilled: 'Despachado',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
};

export const PAYMENT_STATUS_LABEL: Record<string, string> = {
  unpaid: 'Sin pagar',
  partial: 'Pago parcial',
  paid: 'Pagado',
};

export const PO_STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  pending_approval: 'Por aprobar',
  approved: 'Aprobada',
  partially_received: 'Recibida parcial',
  received: 'Recibida',
  cancelled: 'Cancelada',
};

export const PAYMENT_METHOD_LABEL: Record<string, string> = {
  cash: 'Efectivo',
  bank_transfer: 'Transferencia',
  card: 'Tarjeta',
  payment_link: 'Link de pago',
  nequi: 'Nequi',
  daviplata: 'Daviplata',
  pse: 'PSE',
  cash_on_delivery: 'Contra entrega',
  credit: 'Crédito',
  other: 'Otro',
  advance: 'Anticipo',
};

/** Estado de la factura electrónica ante la DIAN. */
export const EINVOICE_STATUS_LABEL: Record<string, string> = {
  not_applicable: 'No aplica',
  pending: 'Enviada, en validación',
  accepted: 'Aceptada por la DIAN',
  rejected: 'Rechazada por la DIAN',
};

/** Decimal para editar en un campo: sin ceros de relleno ("50000.0000" → "50000", "2.5000" → "2.5"). */
export function plainDecimal(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}

/** Estado de la empresa o de su suscripción, como lo lee una persona. */
export const TENANT_STATUS_LABEL: Record<string, string> = {
  trial: 'En prueba',
  trial_expired: 'Prueba terminada',
  active: 'Activa',
  past_due: 'Pago vencido',
  suspended: 'Suspendida',
  cancelled: 'Cancelada',
};

/** "1 pedido", "3 pedidos": singular o plural según la cantidad. */
export function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/**
 * Periodo de un reporte: día («2026-10-09» → «09 oct 2026»), mes («2026-10» → «oct 2026»); otros
 * formatos (semana) se muestran tal cual.
 */
export function formatPeriod(period: string, locale = 'es-CO'): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(period)) return formatDate(period, locale);
  if (/^\d{4}-\d{2}$/.test(period))
    return new Date(`${period}-01T00:00:00`)
      .toLocaleDateString(locale, { month: 'short', year: 'numeric' })
      .replace('.', '');
  return period;
}
